/** @jsxImportSource @emotion/react */
import { FormikHelpers } from 'formik';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';
import { UpsertOrganizerInput } from 'modules/organizer/domain/interfaces/upsert-organizer-input';
import { upsertOrganizer } from 'modules/organizer/store/orginizer/actions';
import { OrganizerForm, OrganizerFormValues } from 'modules/organizer/ui/components/organizer-form';
import { SkeletonOrganizerForm } from 'modules/organizer/ui/components/organizer-form/skeleton-organizer-form';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'store/store';
import { Organizer } from 'modules/organizer/domain/interfaces/organizer';
import { useFetchLogoBackground } from 'shared/hooks/fetchering/use-fetch-logo-background';
import { backgroundColors } from 'shared/constants/backgrounds';
import { useUpdateLogoBackground } from 'shared/hooks/mutations/use-update-logo-background';

function Profile() {
  const dispatch = useDispatch<AppDispatch>();

  const { isLoading: loading, info, isSaving } = useOrganizer();
  const { data: logoBackground, isFetching: isLogoBackgroundFetching } = useFetchLogoBackground();
  const { user } = useAuth();

  const values = useMemo(() => {
    return {
      email: user?.email,
      ...info,
      backgroundColor: backgroundColors[logoBackground?.type ?? 0],
    } as Organizer;
  }, [info, logoBackground, user?.email]);

  const { updateLogoBackground } = useUpdateLogoBackground();

  const handleOrganizerSubmit = useCallback(
    async (data: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) => {
      const { logo, ...restData } = data;

      const params = {
        ...restData,
        adminUserId: user!.id,
        sportType: data.sportType.toString().toUpperCase(),
        logo: logo && logo[0].img instanceof File ? logo[0].img : undefined,
      } as UpsertOrganizerInput;

      // Определяем выбранный цвет фона
      const bgColor = logo && logo[0] ? logo[0].background : undefined;

      try {
        const upsertPromise = dispatch(upsertOrganizer(params)).unwrap();

        const backgroundPromise = updateLogoBackground({
          bgColor,
          tournamentId: info?.id ?? params.id,
        });

        await Promise.allSettled([upsertPromise, backgroundPromise]);
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
    [dispatch, user, info?.id, updateLogoBackground],
  );

  if (loading || isLogoBackgroundFetching) {
    return <SkeletonOrganizerForm />;
  }

  return <OrganizerForm isLoading={isSaving} initialValues={values} onSubmit={handleOrganizerSubmit} />;
}

export default Profile;
